import { useCallback, useMemo, useState } from 'react';
import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { usePreserveModalForm } from '@/components/PreserveModalForm';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useResponse from '@/hooks/useResponse';

import { useRequestPeerFeedbackMutation } from '../../../graphql/mutations/generated/requestFeedback';
import {
  InitialFormValueType,
  RequestFeedbackModalProps,
} from '../components/RequestFeedbackModal/types';
import {
  getFields,
  getSectionLayout,
  initialValues,
  validateMeetingForm,
} from '../components/RequestFeedbackModal/utils';

const useCreateFeedbackRequest = (props: RequestFeedbackModalProps) => {
  const { onClose } = props;
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<InitialFormValueType>(initialValues);
  const { handleResponse } = useResponse();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const { showSnackbar } = useSnackbar();
  const [{ fetching }, sendFeedbackRequest] = useRequestPeerFeedbackMutation();
  const [submitRef, setSubmitRef] = useState<() => void>(() => {});
  const { onFormChange, onCloseModal, modalClosePrompt } = usePreserveModalForm({
    onClose,
  });

  const onSubmit = React.useCallback(async () => {
    const res = await sendFeedbackRequest({
      input: {
        requestedFromUserIds: selectedValues.requestedFromUserIds.map((user) =>
          typeof user === 'string' ? user : user.id,
        ),
        requestMessage: selectedValues.requestMessage,
        skillIds: selectedValues.skillIds,
        mindsetIds: selectedValues.mindsetIds,
      },
    });
    handleResponse({
      condition: !!res.data?.requestPeerFeedback.success,
      onSuccess: () => {
        showSnackbar({
          message: (
            <Trans
              i18nKey={'Feedback:RequestFeedback.SuccessMessage'}
              values={{ count: selectedValues.requestedFromUserIds.length }}
            />
          ),
        });
        onClose();
      },
      onError: () => {
        showDefaultErrorSnackbar();
      },
    });
  }, [
    handleResponse,
    onClose,
    selectedValues.mindsetIds,
    selectedValues.requestMessage,
    selectedValues.requestedFromUserIds,
    selectedValues.skillIds,
    sendFeedbackRequest,
    showDefaultErrorSnackbar,
    showSnackbar,
  ]);

  const onChange = useCallback(
    (response: any) => {
      const { values } = response;
      onFormChange(response);
      setSelectedValues(values);
    },
    [onFormChange],
  );

  const fields = useMemo(() => getFields({ t }), [t]);
  const sections = useMemo(() => getSectionLayout({ t }), [t]);
  const validate = useMemo(
    () => (values: InitialFormValueType) => validateMeetingForm(t, values),
    [t],
  );

  return {
    fields,
    validate,
    sections,
    onChange,
    onSubmit,
    submitRef,
    setSubmitRef,
    fetching,
    selectedValues,
    onCloseModal,
    modalClosePrompt,
  };
};

export default useCreateFeedbackRequest;
